import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, TextField, Autocomplete, CircularProgress } from '@mui/material';
import { newAddressProp } from '../types/type';

interface NewAddressDialogProps {
  open: boolean;
  newAddress: newAddressProp;
  cityOptions: { cityId: number; name: string; provinceId: number; provinceName: string }[];
  isGeocodingLoading: boolean;
  onClose: () => void;
  onInputChange: (field: keyof newAddressProp, value: string | number) => void;
  onCitySearch: (value: string) => void;
  onCitySelect: (event: React.SyntheticEvent, value: any) => void;
  onSave: () => void;
}

export const NewAddressDialog: React.FC<NewAddressDialogProps> = ({
  open,
  newAddress,
  cityOptions,
  isGeocodingLoading,
  onClose,
  onInputChange,
  onCitySearch,
  onCitySelect,
  onSave,
}) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>New Address</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="First Name"
              size="small"
              value={newAddress.addresseeFirstName}
              onChange={(e) => onInputChange('addresseeFirstName', e.target.value)}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Middle Name"
              size="small"
              value={newAddress.addresseeMiddleName}
              onChange={(e) => onInputChange('addresseeMiddleName', e.target.value)}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Last Name"
              size="small"
              value={newAddress.addresseeLastName}
              onChange={(e) => onInputChange('addresseeLastName', e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Unit"
              size="small"
              value={newAddress.unit}
              onChange={(e) => onInputChange('unit', e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Building"
              size="small"
              value={newAddress.building}
              onChange={(e) => onInputChange('building', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Street"
              size="small"
              value={newAddress.street}
              onChange={(e) => onInputChange('street', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Subdivision"
              size="small"
              value={newAddress.subdivision}
              onChange={(e) => onInputChange('subdivision', e.target.value)}
            />
          </Grid>
          <Grid item xs={4}>
            <Autocomplete
              options={cityOptions}
              getOptionLabel={(option) => option.name}
              onInputChange={(event, value) => onCitySearch(value)}
              onChange={onCitySelect}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="City"
                  size="small"
                  fullWidth
                  error={!newAddress.cityId}
                  helperText={!newAddress.cityId ? 'Please select a valid city' : ''}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isGeocodingLoading && <CircularProgress size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              value={cityOptions.find(option => option.cityId === newAddress.cityId) || null}
              isOptionEqualToValue={(option, value) => option.cityId === value?.cityId}
              disabled={isGeocodingLoading}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Zip Code"
              size="small"
              value={newAddress.zipCode}
              onChange={(e) => onInputChange('zipCode', e.target.value)}
            />
          </Grid>
          <Grid item xs={4}></Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Latitude"
              size="small"
              type="number"
              disabled
              value={newAddress.coordinatesLatitude}
              onChange={(e) => onInputChange('coordinatesLatitude', parseFloat(e.target.value) || 0)}
              InputProps={{
                readOnly: isGeocodingLoading,
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Longitude"
              size="small"
              type="number"
              disabled
              value={newAddress.coordinatesLongitude}
              onChange={(e) => onInputChange('coordinatesLongitude', parseFloat(e.target.value) || 0)}
              InputProps={{
                readOnly: isGeocodingLoading,
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          color="secondary"
          disabled={isGeocodingLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          color="primary"
          disabled={isGeocodingLoading}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};